import isEqual from 'lodash/isEqual'
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react'
import { DrilldownExplorationConfiguration, DrilldownViewState } from 'types'

import {
  DEFAULT_EMPTY_CONFIGURATION,
  DEFAULT_EMPTY_VIEW_STATE,
  DrilldownExplorationProviderProps,
} from './DrilldownExplorationContext'
import {
  DEFAULT_EXPLORATION_NAME,
  RolodexDrilldownExplorationState,
  useExplorationFromRolodex,
  useSyncDrilldownStateToRolodex,
} from './rolodexApi/rolodexDrilldownExplorationStateApi'

/**
 * Uses the rolodexDrilldownExplorationStateAPI to load and save
 * drilldown states to a specific ID.
 *
 * Changes made while in presentaiton mode are not written back to
 * Rolodex, and when presentation mode is subsequently disabled,
 * those changes are discarded.
 *
 * @param rolodexId the Rolodex entry to sync to
 * @returns the props to render Drilldown
 */
export function useRolodexBoundDrilldownExplorations(
  rolodexId: string | undefined
): DrilldownExplorationProviderProps & {
  explorationTitle: string
  setExplorationTitle: Dispatch<SetStateAction<string>>
  published: boolean
  setPublished: Dispatch<SetStateAction<boolean>>
} {
  // If we turn on presentation mode, we want to unlink from the underlying rolodex
  // entry, and then snap it back to base when they turn it back off
  const {
    state: rdState,
    setState: setRdState,
    clearState: clearRdState,
    isLoading,
  } = useSyncDrilldownStateToRolodex(rolodexId)

  const { data: dataFromRolodex, isLoading: rolodexLoading } =
    useExplorationFromRolodex(rolodexId)
  useEffect(() => {
    setRdState((prev) =>
      prev.id === rolodexId ||
      !dataFromRolodex ||
      isEqual(prev, dataFromRolodex)
        ? prev
        : dataFromRolodex
    )
  }, [dataFromRolodex, rolodexId, setRdState])

  // Store a separate parallel state when in presentation mode to capture
  // use interactions, and then discard them when presentation mode is disabled.
  const [presentationState, setPresentationState] = useState<
    RolodexDrilldownExplorationState | undefined
  >(undefined)

  const orgId = rdState?.orgId ?? undefined
  const siteId = rdState?.siteId ?? undefined

  const explorationState = rdState?.viewState ?? DEFAULT_EMPTY_VIEW_STATE
  const configuration = rdState?.configuration ?? DEFAULT_EMPTY_CONFIGURATION

  // If we enable presentation mode, we copy the current rolodex state into
  // the presenation state for the user to play around with it.
  const setPresentationModeEnabled = useCallback(
    (val: SetStateAction<boolean>) => {
      setPresentationState((prev) => {
        const newEnabled = typeof val === 'function' ? val(!!prev) : val
        if (newEnabled) {
          return rdState
        } else {
          return undefined
        }
      })
    },
    [rdState]
  )
  const presentationModeEnabled = !!presentationState

  // This is a little annoying, but we need to handle the case
  // where the presentation state is undefined, even though that
  // won't happen in practice - Typescript safety and all.
  const setNonNullPresentationState: Dispatch<
    SetStateAction<RolodexDrilldownExplorationState>
  > = useCallback((val: SetStateAction<RolodexDrilldownExplorationState>) => {
    setPresentationState((prev) => {
      if (typeof val === 'function') {
        const newVal = prev ? val(prev) : prev
        return newVal
      }
      return val
    })
  }, [])

  // Swap out the setter based on whether we're in presentation mode
  const setState = presentationModeEnabled
    ? setNonNullPresentationState
    : setRdState
  const setExplorationState = useCallback(
    (val: SetStateAction<DrilldownViewState>) => {
      setState((prev) => {
        const newVal = typeof val === 'function' ? val(prev.viewState) : val
        if (isEqual(prev.viewState, newVal)) {
          return prev
        }
        return {
          ...prev,
          viewState: newVal,
        }
      })
    },
    [setState]
  )

  const setConfiguration = useCallback(
    (val: SetStateAction<DrilldownExplorationConfiguration>) => {
      setState((prev) => {
        const newVal = typeof val === 'function' ? val(prev.configuration) : val
        if (isEqual(prev.configuration, newVal)) {
          return prev
        }
        return {
          ...prev,
          configuration: newVal,
        }
      })
    },
    [setState]
  )

  const setTitle = useCallback(
    (val: SetStateAction<string>) =>
      setState((prev) => {
        const newVal = typeof val === 'function' ? val(prev.name) : val
        if (isEqual(prev.name, newVal)) {
          return prev
        }
        return {
          ...prev,
          name: newVal,
        }
      }),
    [setState]
  )

  const setPublished: Dispatch<SetStateAction<boolean>> = useCallback(
    (val: SetStateAction<boolean>) => {
      setState((prev) => {
        const newVal = typeof val === 'function' ? val(prev.published) : val
        if (prev.published === newVal) {
          return prev
        }
        return {
          ...prev,
          published: newVal,
        }
      })
    },
    [setState]
  )

  return useMemo(
    () => ({
      orgId: orgId ?? null,
      siteId: siteId ?? null,
      isPresentationModeEnabled: presentationModeEnabled,
      explorationState: presentationState?.viewState ?? explorationState,
      configuration: presentationState?.configuration ?? configuration,
      setIsPresentationModeEnabled: setPresentationModeEnabled,
      setExplorationState,
      setConfiguration,
      isLoading: isLoading || rolodexLoading,
      explorationTitle: rdState?.name ?? DEFAULT_EXPLORATION_NAME,
      setExplorationTitle: setTitle,
      published: rdState?.published ?? false,
      setPublished,
      clearState: clearRdState,
    }),
    [
      clearRdState,
      explorationState,
      configuration,
      isLoading,
      orgId,
      presentationModeEnabled,
      presentationState?.viewState,
      presentationState?.configuration,
      rdState?.name,
      rdState?.published,
      rolodexLoading,
      setExplorationState,
      setConfiguration,
      setTitle,
      setPresentationModeEnabled,
      setPublished,
      siteId,
    ]
  )
}
